/**
 * ImageZoom —— 文章内容图片放大（Lightbox）组件
 *
 * 功能：
 * 1. 点击文章内任意图片（prose 普通图 + Ghost kg-card 图）触发 Lightbox
 * 2. Medium-zoom 风格开场：从图片原位 scale 展开，关闭时缩回
 * 3. 图片切换：水平 Slide + 弹性反馈，无限循环
 * 4. 高清原图加载：优先解析 srcset 中最大宽度变体（1920w），预加载相邻图
 * 5. 移动端：轴向判定（水平 → 切换，垂直 → 平移/关闭），松手弹性回弹
 * 6. 键盘：← → 切换，Escape 关闭
 */

/** 单张图片的上下文 */
interface ZoomImageContext {
  /** 触发图片的低清 src（骨架预览用） */
  previewSrc: string;
  /** 高清原图 URL */
  highResSrc: string;
  /** alt 文字 */
  alt: string;
  /** 触发时图片的视口 BoundingRect（Medium-zoom 开场动画用） */
  rect: DOMRect;
  /** 图片在 images[] 中的序号（0-indexed） */
  index: number;
}

/** 切换方向 */
type NavigateDirection = "prev" | "next";

export class ImageZoom {
  /** 文章内容根容器 */
  private container: HTMLElement;
  /** 所有可放大图片 */
  private images: HTMLImageElement[] = [];

  // ── DOM 引用 ──
  private overlay: HTMLElement | null = null;
  private closeBtn: HTMLElement | null = null;
  private prevBtn: HTMLElement | null = null;
  private nextBtn: HTMLElement | null = null;
  private counter: HTMLElement | null = null;
  /** 图片轨道容器（用于 Slide 动画） */
  private track: HTMLElement | null = null;
  /** 当前展示的 <img> */
  private zoomImage: HTMLImageElement | null = null;
  /** 骨架屏 */
  private skeleton: HTMLElement | null = null;
  /** caption 文字 */
  private caption: HTMLElement | null = null;

  // ── 状态 ──
  private currentCtx: ZoomImageContext | null = null;
  private isOpen = false;
  private isAnimatingClose = false;
  /** 是否正在执行图片切换（防抖） */
  private isNavigating = false;

  // ── 高清图预加载缓存（index → objectURL 或原始 src） ──
  private preloadCache = new Map<number, string>();

  // ── 移动端手势状态 ──
  private touchStartX = 0;
  private touchStartY = 0;
  /** 手势轴向锁定：null=未确定，'x'=水平切换，'y'=垂直平移 */
  private touchAxis: "x" | "y" | null = null;
  /** 水平拖拽位移（跟踪弹性反馈） */
  private touchDeltaX = 0;
  /** 轴向判定阈值（px） */
  private readonly AXIS_THRESHOLD = 8;
  /** 触发切换的最小滑动距离（px） */
  private readonly SWIPE_THRESHOLD = 60;
  /** 弹性边界阻力系数（0~1，越小越难拉） */
  private readonly RESISTANCE = 0.35;

  constructor(container: HTMLElement) {
    this.container = container;
    this.bindImages();
  }

  // ─────────────────────────────────────────────
  // 初始化：绑定图片
  // ─────────────────────────────────────────────

  /**
   * 选取文章内所有图片并绑定点击事件
   */
  private bindImages(): void {
    const imgs = this.container.querySelectorAll<HTMLImageElement>(
      'img:not([width="1"]):not([aria-hidden="true"])',
    );

    imgs.forEach((img, idx) => {
      if (!img.src && !img.srcset) return;

      img.classList.add("img-zoomable");
      img.setAttribute("data-zoom-index", String(idx));
      img.addEventListener("click", this.handleImageClick);
      this.images.push(img);
    });

    console.log(`[ImageZoom] 绑定了 ${this.images.length} 张可放大图片`);
  }

  // ─────────────────────────────────────────────
  // 事件处理
  // ─────────────────────────────────────────────

  private handleImageClick = (e: Event): void => {
    e.preventDefault();
    const img = e.currentTarget as HTMLImageElement;
    this.open({
      previewSrc: img.src,
      highResSrc: this.getHighResUrl(img),
      alt: img.alt || "",
      rect: img.getBoundingClientRect(),
      index: parseInt(img.dataset.zoomIndex ?? "0", 10),
    });
  };

  private handleKeyDown = (e: KeyboardEvent): void => {
    switch (e.key) {
      case "Escape":
        this.close();
        break;
      case "ArrowLeft":
        e.preventDefault();
        this.navigate("prev");
        break;
      case "ArrowRight":
        e.preventDefault();
        this.navigate("next");
        break;
    }
  };

  private handleOverlayClick = (e: MouseEvent): void => {
    if (e.target === this.overlay) this.close();
  };

  // ─────────────────────────────────────────────
  // 高清原图 URL 解析
  // ─────────────────────────────────────────────

  /**
   * 从 srcset 解析最大宽度（1920w）变体 URL；无 srcset 则用 img.src
   */
  private getHighResUrl(img: HTMLImageElement): string {
    const srcset = img.srcset || img.getAttribute("srcset") || "";
    if (!srcset) return img.src;

    const entries = srcset
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => {
        const parts = s.split(/\s+/);
        return {
          url: parts[0] ?? "",
          width: parseInt((parts[1] ?? "").replace("w", ""), 10) || 0,
        };
      })
      .filter((e) => e.url && e.width > 0);

    if (!entries.length) return img.src;

    entries.sort((a, b) => b.width - a.width);
    const best = entries[0];
    console.log(`[ImageZoom] 高清 URL: ${best?.url} (${best?.width}w)`);
    return best?.url ?? img.src;
  }

  // ─────────────────────────────────────────────
  // Lightbox 开启
  // ─────────────────────────────────────────────

  private open(ctx: ZoomImageContext): void {
    if (this.isOpen || this.isAnimatingClose) return;
    this.isOpen = true;
    this.currentCtx = ctx;

    document.body.style.overflow = "hidden";
    this.buildDOM(ctx);

    // 事件绑定
    document.addEventListener("keydown", this.handleKeyDown);
    this.overlay?.addEventListener("click", this.handleOverlayClick);
    this.closeBtn?.addEventListener("click", () => this.close());
    this.prevBtn?.addEventListener("click", () => this.navigate("prev"));
    this.nextBtn?.addEventListener("click", () => this.navigate("next"));

    // 移动端手势（绑定到 overlay 以捕获全屏滑动）
    this.overlay?.addEventListener("touchstart", this.handleTouchStart, {
      passive: true,
    });
    this.overlay?.addEventListener("touchmove", this.handleTouchMove, {
      passive: false,
    });
    this.overlay?.addEventListener("touchend", this.handleTouchEnd);

    // 开场动画
    requestAnimationFrame(() => {
      this.overlay?.classList.add("is-opening");
      this.track?.classList.add("is-opening");
    });

    setTimeout(() => this.closeBtn?.focus(), 50);

    // 加载当前图 + 预加载相邻图
    this.loadHighRes(ctx, this.zoomImage!);
    this.preloadAdjacent(ctx.index);
  }

  // ─────────────────────────────────────────────
  // DOM 构建
  // ─────────────────────────────────────────────

  private buildDOM(ctx: ZoomImageContext): void {
    const { rect } = ctx;
    const vpW = window.innerWidth;
    const vpH = window.innerHeight;

    // Medium-zoom 开场：计算从图片原位展开的 transform
    const originX = rect.left + rect.width / 2 - vpW / 2;
    const originY = rect.top + rect.height / 2 - vpH / 2;
    const displayW = Math.min(rect.width, vpW * 0.9);
    const scaleX = rect.width / displayW;
    const scaleY = rect.height / Math.min(rect.height, vpH * 0.9);
    const scale = Math.max(scaleX, scaleY, 0.1);

    // ── overlay ──
    const overlay = document.createElement("div");
    overlay.className = "img-zoom-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", ctx.alt || "图片预览");
    overlay.style.setProperty(
      "--zoom-origin-transform",
      `scale(${scale.toFixed(3)}) translate(${originX.toFixed(1)}px, ${originY.toFixed(1)}px)`,
    );

    // ── 关闭按钮 ──
    const closeBtn = document.createElement("button");
    closeBtn.className = "img-zoom-close";
    closeBtn.setAttribute("aria-label", "关闭图片预览");
    closeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`;
    overlay.appendChild(closeBtn);

    // ── 导航箭头（图片数 > 1 才显示） ──
    let prevBtn: HTMLElement | null = null;
    let nextBtn: HTMLElement | null = null;
    if (this.images.length > 1) {
      prevBtn = this.createNavBtn("prev");
      nextBtn = this.createNavBtn("next");
      overlay.appendChild(prevBtn);
      overlay.appendChild(nextBtn);
    }

    // ── 计数器 ──
    let counter: HTMLElement | null = null;
    if (this.images.length > 1) {
      counter = document.createElement("div");
      counter.className = "img-zoom-counter";
      counter.textContent = `${ctx.index + 1} / ${this.images.length}`;
      overlay.appendChild(counter);
    }

    // ── 图片轨道（Slide 动画容器） ──
    const track = document.createElement("div");
    track.className = "img-zoom-track";

    // ── 骨架屏 ──
    const skeleton = document.createElement("div");
    skeleton.className = "img-zoom-skeleton";
    const aspectRatio = rect.height / (rect.width || 1);
    const estimatedW = Math.min(rect.width * 2, vpW * 0.9);
    skeleton.style.width = `${estimatedW}px`;
    skeleton.style.height = `${Math.min(estimatedW * aspectRatio, vpH * 0.9)}px`;
    track.appendChild(skeleton);

    // ── 展示图片（低清占位） ──
    const zoomImg = document.createElement("img");
    zoomImg.className = "img-zoom-image is-loading";
    zoomImg.src = ctx.previewSrc;
    zoomImg.alt = ctx.alt;
    zoomImg.draggable = false;
    track.appendChild(zoomImg);

    // ── caption ──
    const caption = document.createElement("div");
    caption.className = "img-zoom-caption";
    caption.textContent = ctx.alt || "";
    caption.style.visibility = ctx.alt ? "visible" : "hidden";
    track.appendChild(caption);

    overlay.appendChild(track);
    document.body.appendChild(overlay);

    // 缓存引用
    this.overlay = overlay;
    this.closeBtn = closeBtn;
    this.prevBtn = prevBtn;
    this.nextBtn = nextBtn;
    this.counter = counter;
    this.track = track;
    this.zoomImage = zoomImg;
    this.skeleton = skeleton;
    this.caption = caption;
  }

  /**
   * 创建导航箭头按钮
   */
  private createNavBtn(dir: NavigateDirection): HTMLElement {
    const btn = document.createElement("button");
    btn.className = `img-zoom-nav img-zoom-nav--${dir}`;
    btn.setAttribute("aria-label", dir === "prev" ? "上一张" : "下一张");
    // 左/右箭头 SVG
    const d = dir === "prev" ? "M15 18l-6-6 6-6" : "M9 18l6-6-6-6";
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="${d}"/></svg>`;
    return btn;
  }

  // ─────────────────────────────────────────────
  // 高清图加载与预加载
  // ─────────────────────────────────────────────

  /**
   * 异步加载高清原图，完成后替换低清预览
   * @param ctx 目标图片上下文
   * @param targetEl 要替换 src 的 <img> 元素
   */
  private loadHighRes(ctx: ZoomImageContext, targetEl: HTMLImageElement): void {
    // 已在缓存中
    const cached = this.preloadCache.get(ctx.index);
    if (cached) {
      this.applyHighRes(targetEl, cached);
      return;
    }

    if (ctx.highResSrc === ctx.previewSrc) {
      this.applyHighRes(targetEl, ctx.previewSrc);
      return;
    }

    const hq = new Image();
    hq.onload = () => {
      this.preloadCache.set(ctx.index, ctx.highResSrc);
      // 仅当 targetEl 仍是当前展示的图片时才替换（防止切换期间错位）
      if (targetEl === this.zoomImage && this.isOpen) {
        this.applyHighRes(targetEl, ctx.highResSrc);
      }
    };
    hq.onerror = () => {
      console.warn("[ImageZoom] 高清图加载失败，使用低清预览");
      if (targetEl === this.zoomImage && this.isOpen) {
        this.applyHighRes(targetEl);
      }
    };
    hq.src = ctx.highResSrc;
  }

  private applyHighRes(img: HTMLImageElement, src?: string): void {
    if (src) img.src = src;
    this.skeleton?.remove();
    this.skeleton = null;
    img.classList.remove("is-loading");
    img.classList.add("is-loaded");
  }

  /**
   * 预加载相邻 ±1 张图片（无限循环取模）
   */
  private preloadAdjacent(index: number): void {
    const total = this.images.length;
    if (total <= 1) return;

    const prevIdx = (index - 1 + total) % total;
    const nextIdx = (index + 1) % total;

    for (const idx of [prevIdx, nextIdx]) {
      if (this.preloadCache.has(idx)) continue;
      const img = this.images[idx];
      if (!img) continue;
      const hqSrc = this.getHighResUrl(img);
      if (hqSrc === img.src) {
        this.preloadCache.set(idx, hqSrc);
        continue;
      }
      const hq = new Image();
      hq.onload = () => this.preloadCache.set(idx, hqSrc);
      hq.src = hqSrc;
    }
  }

  // ─────────────────────────────────────────────
  // 图片切换（导航核心）
  // ─────────────────────────────────────────────

  /**
   * 切换到上一张或下一张（无限循环）
   * @param dir 方向
   * @param slideOverride 可覆盖 slide 距离（移动端手势传入实际偏移）
   */
  private navigate(dir: NavigateDirection, slideOverride?: number): void {
    if (!this.isOpen || this.isNavigating || !this.currentCtx || !this.track)
      return;
    if (this.images.length <= 1) return;

    this.isNavigating = true;

    const total = this.images.length;
    const currentIndex = this.currentCtx.index;
    const nextIndex =
      dir === "next"
        ? (currentIndex + 1) % total
        : (currentIndex - 1 + total) % total;

    const nextImg = this.images[nextIndex];
    if (!nextImg) {
      this.isNavigating = false;
      return;
    }

    // 1. 构建新图 <img> 元素（预置在轨道右侧或左侧）
    const newZoomImg = document.createElement("img");
    newZoomImg.className = "img-zoom-image img-zoom-image--incoming";
    newZoomImg.src = this.preloadCache.get(nextIndex) ?? nextImg.src;
    newZoomImg.alt = nextImg.alt || "";
    newZoomImg.draggable = false;

    // 新图的初始位移：next 从右进，prev 从左进
    const slideDir = dir === "next" ? 1 : -1;
    newZoomImg.style.transform = `translateX(${slideDir * 100}%)`;
    this.track.appendChild(newZoomImg);

    // 2. 如果是手势触发，有已有的偏移量；否则从 0 开始
    const _startOffset = slideOverride ?? 0;

    // 3. 在下一帧启动动画
    requestAnimationFrame(() => {
      // 当前图滑出
      if (this.zoomImage) {
        this.zoomImage.style.transition =
          "transform 380ms cubic-bezier(0.25, 0.46, 0.45, 0.94)";
        this.zoomImage.style.transform = `translateX(${-slideDir * 100}%)`;
      }
      // 新图滑入
      newZoomImg.style.transition =
        "transform 380ms cubic-bezier(0.25, 0.46, 0.45, 0.94)";
      newZoomImg.style.transform = "translateX(0)";
    });

    // 4. 动画结束后清理旧图，更新状态
    const oldImg = this.zoomImage;
    const oldSkeleton = this.skeleton;

    setTimeout(() => {
      oldImg?.remove();
      oldSkeleton?.remove();
      this.skeleton = null;

      // 更新引用和状态
      this.zoomImage = newZoomImg;
      newZoomImg.classList.remove("img-zoom-image--incoming");
      newZoomImg.style.transition = "";
      newZoomImg.style.transform = "";

      // 更新上下文
      this.currentCtx = {
        previewSrc: nextImg.src,
        highResSrc:
          this.preloadCache.get(nextIndex) ?? this.getHighResUrl(nextImg),
        alt: nextImg.alt || "",
        rect: nextImg.getBoundingClientRect(),
        index: nextIndex,
      };

      // 更新 caption
      if (this.caption) {
        this.caption.textContent = this.currentCtx.alt;
        this.caption.style.visibility = this.currentCtx.alt
          ? "visible"
          : "hidden";
      }

      // 更新计数器
      if (this.counter) {
        this.counter.textContent = `${nextIndex + 1} / ${total}`;
      }

      // 加载高清图（若未缓存）
      if (!newZoomImg.classList.contains("is-loaded")) {
        const hqSrc = this.preloadCache.get(nextIndex);
        if (hqSrc && hqSrc !== newZoomImg.src) {
          this.applyHighRes(newZoomImg, hqSrc);
        } else {
          this.loadHighRes(this.currentCtx, newZoomImg);
        }
      }

      // 预加载下下张
      this.preloadAdjacent(nextIndex);

      this.isNavigating = false;
    }, 400);
  }

  // ─────────────────────────────────────────────
  // 关闭
  // ─────────────────────────────────────────────

  close(): void {
    if (!this.isOpen || this.isAnimatingClose) return;
    this.isAnimatingClose = true;

    this.overlay?.classList.remove("is-opening");
    this.overlay?.classList.add("is-closing");
    this.track?.classList.remove("is-opening");
    this.track?.classList.add("is-closing");

    setTimeout(() => {
      this.destroyDOM();
      document.body.style.overflow = "";
      document.removeEventListener("keydown", this.handleKeyDown);
      this.isOpen = false;
      this.isAnimatingClose = false;
      this.currentCtx = null;
    }, 280);
  }

  private destroyDOM(): void {
    this.overlay?.remove();
    this.overlay = null;
    this.closeBtn = null;
    this.prevBtn = null;
    this.nextBtn = null;
    this.counter = null;
    this.track = null;
    this.zoomImage = null;
    this.skeleton = null;
    this.caption = null;
  }

  // ─────────────────────────────────────────────
  // 移动端手势（轴向判定）
  // ─────────────────────────────────────────────

  private handleTouchStart = (e: TouchEvent): void => {
    const touch = e.touches[0];
    if (!touch) return;

    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchAxis = null;
    this.touchDeltaX = 0;

    // 禁用轨道过渡（直接跟随手指）
    if (this.track) this.track.style.transition = "none";
    if (this.zoomImage) this.zoomImage.style.transition = "none";
  };

  private handleTouchMove = (e: TouchEvent): void => {
    const touch = e.touches[0];
    if (!touch) return;

    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    // 轴向判定（首次超过阈值时锁定）
    if (!this.touchAxis) {
      if (
        Math.abs(deltaX) < this.AXIS_THRESHOLD &&
        Math.abs(deltaY) < this.AXIS_THRESHOLD
      )
        return;
      this.touchAxis = Math.abs(deltaX) >= Math.abs(deltaY) ? "x" : "y";
    }

    if (this.touchAxis === "x") {
      e.preventDefault();

      // 仅 1 张图时：弹性边界（无法切换）
      if (this.images.length <= 1) {
        const resistance = deltaX * this.RESISTANCE;
        if (this.zoomImage) {
          this.zoomImage.style.transform = `translateX(${resistance}px)`;
        }
        this.touchDeltaX = deltaX;
        return;
      }

      // 正常水平拖拽跟随手指
      if (this.zoomImage) {
        this.zoomImage.style.transform = `translateX(${deltaX}px)`;
      }
      this.touchDeltaX = deltaX;
    } else {
      // 垂直拖拽：整体平移（方便查看图片）
      e.preventDefault();
      if (this.track) {
        this.track.style.transform = `translateY(${deltaY * 0.5}px)`;
      }
    }
  };

  private handleTouchEnd = (): void => {
    const deltaX = this.touchDeltaX;
    const axis = this.touchAxis;

    // 恢复过渡
    const ease = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";

    if (axis === "x") {
      const absX = Math.abs(deltaX);

      if (this.images.length <= 1) {
        // 弹回中心
        if (this.zoomImage) {
          this.zoomImage.style.transition = `transform 350ms ${ease}`;
          this.zoomImage.style.transform = "translateX(0)";
        }
      } else if (absX >= this.SWIPE_THRESHOLD) {
        // 超过阈值 → 触发切换，传入当前偏移量给 navigate 用于连贯动画起点
        const dir: NavigateDirection = deltaX < 0 ? "next" : "prev";
        // 先把当前图还原（navigate 内部会重新动画）
        if (this.zoomImage) {
          this.zoomImage.style.transition = "";
          this.zoomImage.style.transform = "";
        }
        this.navigate(dir);
      } else {
        // 未达阈值 → 弹性回弹
        if (this.zoomImage) {
          this.zoomImage.style.transition = `transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1)`;
          this.zoomImage.style.transform = "translateX(0)";
        }
      }
    } else {
      // 垂直方向：track 回弹
      if (this.track) {
        this.track.style.transition = `transform 350ms ${ease}`;
        this.track.style.transform = "translateY(0)";
      }
    }

    // 重置手势状态
    this.touchAxis = null;
    this.touchDeltaX = 0;
  };

  // ─────────────────────────────────────────────
  // 销毁
  // ─────────────────────────────────────────────

  destroy(): void {
    this.images.forEach((img) => {
      img.removeEventListener("click", this.handleImageClick);
      img.classList.remove("img-zoomable");
      img.removeAttribute("data-zoom-index");
    });
    this.images = [];
    this.preloadCache.clear();

    if (this.isOpen) {
      this.destroyDOM();
      document.body.style.overflow = "";
      document.removeEventListener("keydown", this.handleKeyDown);
      this.isOpen = false;
    }

    console.log("[ImageZoom] 组件已销毁");
  }
}
